import { clone } from '../../../webmodule-common/other/clone';
import { DevelopmentError } from '../../../webmodule-common/other/development-error';
import { EventBoolean, EventNotify, NullPromise } from '../../../webmodule-common/other/ui/events';
import { FabricationJob, FabricationJobItem, FabricationJobState } from '../../api/fabrication-job-api';
import { getApiFactory } from '../../api/api-injector';
import { runEventNotify } from '../../../webmodule-common/other/array-helper';

export class FabricationJobContainer {
  fabricationJobId: string;
  fabricationJob: FabricationJob | null;
  items: FabricationJobItem[];

  constructor(fabricationJobId: string, fabricationJob: FabricationJob | null, items: FabricationJobItem[]) {
    this.fabricationJobId = fabricationJobId;
    this.fabricationJob = fabricationJob;
    this.items = items;
  }
}

export class FabricationJobContainerManager {
  container: FabricationJobContainer;
  backup: FabricationJobContainer;
  api = getApiFactory().fabricationJob();
  afterSave: EventNotify[] = [];
  forceLocked?: EventBoolean;

  constructor(original: FabricationJobContainer) {
    this.container = original;
    this.backup = clone(this.container);
  }

  get fabricationJobId(): string {
    return this.container.fabricationJobId;
  }

  get fabricationJob(): FabricationJob {
    if (!this.container.fabricationJob) throw new DevelopmentError('FabricationJob is null');
    return this.container.fabricationJob;
  }

  get items(): FabricationJobItem[] {
    return this.container.items;
  }

  get isDraft(): boolean {
    return this.fabricationJob.state === FabricationJobState.Draft;
  }

  isLockedFromUse(): boolean {
    return this.forceLocked?.() ?? false;
  }

  isReadOnly(): boolean {
    return this.isLockedFromUse() || !this.isDraft;
  }

  jobChanged(): boolean {
    const current = this.container.fabricationJob;
    const original = this.backup.fabricationJob;
    if (!current || !original) return false;
    return current.title !== original.title || current.description !== original.description;
  }

  async needsFabricationJob(): Promise<void> {
    if (this.container.fabricationJob) return;
    await this.loadFromServer();
  }

  async loadFromServer(): Promise<void> {
    const result = await this.api.getFabricationJob({ id: this.fabricationJobId });
    if (result) {
      this.container.fabricationJob = result.fabricationJob;
      this.container.items = result.items;
      this.backup = clone(this.container);
    }
  }

  async updateJob(title: string, description: string): NullPromise<FabricationJob> {
    const result = await this.api.updateFabricationJob({
      id: this.fabricationJob.id,
      recordVersion: this.fabricationJob.recordVersion,
      title,
      description
    });
    if (result) {
      this.container.fabricationJob = result.fabricationJob;
      this.backup = clone(this.container);
      await this.doAfterSave();
    }
    return result?.fabricationJob ?? null;
  }

  async deleteJob(): Promise<boolean> {
    const result = await this.api.deleteFabricationJob({
      id: this.fabricationJob.id,
      recordVersion: this.fabricationJob.recordVersion
    });
    if (result) {
      await this.doAfterSave();
      return true;
    }
    return false;
  }

  async addItem(quoteItemId: string): NullPromise<FabricationJob> {
    const result = await this.api.addQuoteItemToFabricationJob({
      fabricationJobId: this.fabricationJob.id,
      quoteItemId,
      fabricationJobRecordVersion: this.fabricationJob.recordVersion
    });
    if (result) {
      this.container.fabricationJob = result.fabricationJob;
      await this.loadFromServer(); // reload items
      await this.doAfterSave();
    }
    return result?.fabricationJob ?? null;
  }

  async removeItem(quoteItemId: string): NullPromise<FabricationJob> {
    const result = await this.api.removeQuoteItemFromFabricationJob({
      fabricationJobId: this.fabricationJob.id,
      quoteItemId,
      fabricationJobRecordVersion: this.fabricationJob.recordVersion
    });
    if (result) {
      this.container.fabricationJob = result.fabricationJob;
      await this.loadFromServer(); // reload items
      await this.doAfterSave();
    }
    return result?.fabricationJob ?? null;
  }

  protected async doAfterSave(): Promise<void> {
    await runEventNotify(this.afterSave);
  }
}
